import {
  FRAMES, FRAME_SURCHARGES, sizeKey, resolveVariant, formatCurrency,
} from "@/lib/canvasConfig";

const getFrameSurchargeAmount = (frameId) => FRAME_SURCHARGES[frameId] ?? 0;

/**
 * Size + Frame selector for a canvas product.
 * Controlled: parent owns activeSizeKey / selectedFrame / previewMode and
 * passes `getPresetPriceData(sizeObj)` for live pricing indicators.
 */
export default function CanvasSizeFrameSelector({
  product,
  sizes,
  activeSizeKey,
  setSelectedSize,
  selectedFrame,
  setSelectedFrame,
  previewMode,
  setPreviewMode,
  activeSizeObj,
  isCustomSize,
  hasImage,
  getPresetPriceData,
}) {
  return (
    <>
      {/* ── SIZE ── */}
      <div className="mt-8" data-testid="size-selector">
        <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-brand-text-secondary/70 mb-3">
          Size
        </p>
        <div className="flex flex-wrap gap-2">
          {sizes.map((s) => {
            const key = sizeKey(s);
            const sizePrice = getPresetPriceData(s);
            const isLive = sizePrice?.source === "variant";
            const sizeAvailable = sizePrice?.available;
            const activeCls = activeSizeKey === key
              ? "border-brand-gold text-brand-gold bg-brand-gold/5"
              : "border-brand-border text-brand-text-secondary hover:border-brand-text-secondary/40";
            const dimCls = isLive && sizeAvailable === false ? "opacity-50" : "";
            return (
              <button
                key={key}
                onClick={() => setSelectedSize(key)}
                className={`relative px-4 py-2.5 rounded-sm text-[12px] font-medium transition-[border-color,color,background-color,opacity] duration-200 border min-w-[72px] text-center ${activeCls} ${dimCls}`}
                data-testid={`size-${key}`}
                data-available={isLive ? String(sizeAvailable) : undefined}
              >
                <span className="tabular-nums">{s.w} <span className="opacity-50">&times;</span> {s.h}</span>
                <span className="block text-[10px] font-normal mt-0.5 opacity-40">cm</span>
                {sizePrice && (
                  <span className={`block text-[10px] font-medium mt-1 ${isLive && sizeAvailable === false ? "line-through opacity-40" : "opacity-70"}`}>
                    {formatCurrency(sizePrice.amount, sizePrice.currencyCode)}
                  </span>
                )}
                {isLive && (
                  <span
                    className={`absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full ${sizeAvailable !== false ? "bg-emerald-400/70" : "bg-brand-text-secondary/25"}`}
                    data-testid={`size-${key}-availability`}
                  />
                )}
              </button>
            );
          })}
          <button
            onClick={() => setSelectedSize("Custom")}
            className={`px-4 py-2.5 rounded-sm text-[12px] font-medium transition-[border-color,color,background-color] duration-200 border min-w-[72px] text-center ${isCustomSize ? "border-brand-gold text-brand-gold bg-brand-gold/5" : "border-brand-border text-brand-text-secondary hover:border-brand-text-secondary/40"}`}
            data-testid="size-Custom"
          >
            Custom
            <span className="block text-[10px] font-normal mt-0.5 opacity-40">any size</span>
            <span className="block text-[10px] font-medium mt-1 opacity-70">Quote</span>
          </button>
        </div>
      </div>

      {/* ── FRAME ── */}
      {hasImage && (
        <div className="mt-6" data-testid="frame-selector">
          <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-brand-text-secondary/70 mb-3">
            Frame
          </p>
          <div className="flex flex-wrap gap-2">
            {FRAMES.map((f) => {
              const frameVariant = activeSizeObj
                ? resolveVariant(product.variants, activeSizeObj.shopifyOption, f.shopifyOption)
                : null;
              const isLiveFrame = frameVariant != null;
              const frameAvailable = frameVariant?.availableForSale !== false;
              const activeCls = selectedFrame === f.id
                ? "border-brand-gold text-brand-gold bg-brand-gold/5"
                : "border-brand-border text-brand-text-secondary hover:border-brand-text-secondary/40";
              const dimCls = isLiveFrame && !frameAvailable ? "opacity-50" : "";
              return (
                <button
                  key={f.id}
                  onClick={() => {
                    setSelectedFrame(f.id);
                    if (f.id !== "none" && previewMode === "artwork") {
                      setPreviewMode("framed");
                    }
                    if (f.id === "none" && previewMode === "framed") {
                      setPreviewMode("artwork");
                    }
                  }}
                  className={`relative px-4 py-2.5 rounded-sm text-[12px] font-medium transition-[border-color,color,background-color,opacity] duration-200 border ${activeCls} ${dimCls}`}
                  data-testid={`frame-${f.id}`}
                  data-available={isLiveFrame ? String(frameAvailable) : undefined}
                >
                  {f.label}
                  <span className="block text-[10px] font-normal mt-0.5 opacity-50">
                    {getFrameSurchargeAmount(f.id) === 0
                      ? "Included"
                      : `+£${getFrameSurchargeAmount(f.id)}`}
                  </span>
                  {isLiveFrame && (
                    <span
                      className={`absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full ${frameAvailable ? "bg-emerald-400/70" : "bg-brand-text-secondary/25"}`}
                      data-testid={`frame-${f.id}-availability`}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
